/**
 * Phase 20 — the country's own auto-send setting: how many clean send
 * failures in a row halt auto-send for every campaign group (a send whose
 * outcome is unknown halts at once). Saved on its own, with its own button.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useEffect, useState } from 'react';

import { Button, NumberField } from '@/components/ui';
import { getApiErrorMessage } from '@/lib/utils';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/providers/ToastProvider';

import type { DropGenPlanDetail } from '../../../../drop-generator.types';
import { useAutoSendMutations } from '../use-autosend-mutations';

export const CountryLimitField: FC<{ plan: DropGenPlanDetail }> = ({ plan }) => {
  const { hasPermission } = useAuth();
  const { success, error } = useToast();
  const { country } = useAutoSendMutations(plan.countryKey);
  const [value, setValue] = useState(plan.maxSendFailures);
  useEffect(() => setValue(plan.maxSendFailures), [plan.maxSendFailures]);
  const canEdit = hasPermission('drop_generator.autosend');
  const save = async () => {
    try {
      await country.mutateAsync({ maxSendFailures: value });
      success(`Auto-send halts after ${value} failed sends in a row`);
    } catch (err) {
      error('Not saved', getApiErrorMessage(err));
    }
  };
  return (
    <span className="flex flex-wrap items-center gap-2">
      <span className="text-muted-foreground">Halt every group after</span>
      <NumberField
        value={value}
        min={1}
        max={20}
        onChange={setValue}
        disabled={!canEdit}
        aria-label="Failed sends in a row before every group halts"
        className="h-7 w-14 rounded-md border border-input bg-background px-2 text-xs font-semibold tabular-nums disabled:opacity-40"
      />
      <span className="text-muted-foreground">failed sends in a row</span>
      {value !== plan.maxSendFailures ? (
        <Button size="sm" variant="outline" loading={country.isPending} disabled={!canEdit} onClick={() => void save()}>Save</Button>
      ) : null}
    </span>
  );
};
